import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActivationJourneyService } from './activation-journey.service';

export const ACTIVATION_EMPLOYEE_HANDOFF_KEY = 'supersee_activation_employee_handoff';
export const ACTIVATION_EMPLOYEE_HANDOFF_TTL_MS = 30 * 60 * 1000;

export type ActivationEmployeeHandoffPhase = 'creating' | 'created';

export interface ActivationEmployeeHandoffSession {
  source: 'activation_journey';
  phase: ActivationEmployeeHandoffPhase;
  returnRoute: '/setup/first-employee';
  startedAt: number;
  employeeId?: number;
  versionType?: 'general' | 'restricted' | 'silent';
  operatingSystem?: 'windows' | 'mac' | 'linux' | 'windows_7_8';
}

@Injectable({ providedIn: 'root' })
export class ActivationEmployeeHandoffService {
  constructor(
    private router: Router,
    private activationJourney: ActivationJourneyService
  ) {}

  begin(): ActivationEmployeeHandoffSession {
    const session: ActivationEmployeeHandoffSession = {
      source: 'activation_journey',
      phase: 'creating',
      returnRoute: '/setup/first-employee',
      startedAt: Date.now(),
    };
    this.write(session);
    return session;
  }

  isActive(): boolean {
    return !!this.readValid();
  }

  getSession(): ActivationEmployeeHandoffSession | null {
    return this.readValid();
  }

  recordCreated(opts: {
    employeeId: number;
    versionType: 'general' | 'restricted' | 'silent';
    operatingSystem: 'windows' | 'mac' | 'linux' | 'windows_7_8';
  }): void {
    const current = this.readValid();
    if (!current) {
      return;
    }
    this.write({
      ...current,
      phase: 'created',
      employeeId: opts.employeeId,
      versionType: opts.versionType,
      operatingSystem: opts.operatingSystem,
    });
    this.activationJourney.trackEvent('invite_sent', {
      employeeId: opts.employeeId,
      operatingSystem: opts.operatingSystem,
    });
  }

  cancel(): void {
    this.clear();
    this.activationJourney.clearInviteFirstAutoOpen();
  }

  finishReturn(): void {
    this.clear();
  }

  /** Navigate into Employees create modal while handoff is active. */
  launchEmployeesCreate(): void {
    if (!this.isActive()) {
      this.begin();
    }
    void this.router.navigate(['/employees'], {
      queryParams: {
        'create-employee': 'true',
        activationHandoff: 'first_employee',
      },
    });
  }

  /** Return to setup recovery without mutating firstEmployeeId. */
  returnToSetup(replaceUrl = true): void {
    const returnRoute = this.readValid()?.returnRoute || '/setup/first-employee';
    this.clear();
    // Drop Ready-era invite-first so Cancel/hard-refresh cannot reopen the modal.
    this.activationJourney.clearInviteFirstAutoOpen();
    void this.router.navigate([returnRoute], { replaceUrl });
  }

  private readValid(): ActivationEmployeeHandoffSession | null {
    try {
      const raw = sessionStorage.getItem(ACTIVATION_EMPLOYEE_HANDOFF_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as ActivationEmployeeHandoffSession;
      if (
        !parsed ||
        parsed.source !== 'activation_journey' ||
        parsed.returnRoute !== '/setup/first-employee' ||
        (parsed.phase !== 'creating' && parsed.phase !== 'created') ||
        typeof parsed.startedAt !== 'number'
      ) {
        this.clear();
        return null;
      }
      if (Date.now() - parsed.startedAt > ACTIVATION_EMPLOYEE_HANDOFF_TTL_MS) {
        this.clear();
        return null;
      }
      return parsed;
    } catch {
      this.clear();
      return null;
    }
  }

  private write(session: ActivationEmployeeHandoffSession): void {
    try {
      sessionStorage.setItem(ACTIVATION_EMPLOYEE_HANDOFF_KEY, JSON.stringify(session));
    } catch {
      /* ignore quota / private mode */
    }
  }

  private clear(): void {
    try {
      sessionStorage.removeItem(ACTIVATION_EMPLOYEE_HANDOFF_KEY);
    } catch {
      /* ignore */
    }
  }
}
